import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles, Typography, Paper, Tooltip } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { AccessTime } from '@material-ui/icons';
import TimelineConnector from '@material-ui/lab/TimelineConnector';
import { TimelineItem, TimelineContent, TimelineOppositeContent, TimelineSeparator, TimelineDot } from '@material-ui/lab';
import { CONSTANTS } from '../../../constants/constants';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: '6px 16px'
  },
  secondaryTail: {
    backgroundColor: theme.palette.secondary.main
  },
  textLine: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'baseline'
  }

}));

const Separator = () => {
  return (
    <TimelineSeparator>
      <TimelineDot>
        <AccessTime />
      </TimelineDot>
      <TimelineConnector />
    </TimelineSeparator>
  );
};

const AuditDetail = ({ audit, type }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  let snsData = audit.snsData;
  if (audit.snsData && audit.snsData.Message) {
    try {
      snsData = { ...snsData, ...JSON.parse(audit.snsData.Message) };
    } catch (e) {
      console.log(e);
    }
  } else if (audit.snsData) {
    try {
      snsData = { ...snsData, ...JSON.parse(audit.snsData) };
    } catch (e) {
      console.log(e);
    }
  } else {
    snsData = {};
  }
  const showNumberOfSplitMessages = (snsData) => {
    if (snsData.rawResponse) {
      const rawResponse = snsData.rawResponse;
      const numberOfTheMessage = parseInt(rawResponse.id.split('$')[1]);
      const numberNotToShowZero = 1;
      const totalOfMessagesParts = snsData.delivery.numberOfMessageParts;
      return (<div className={classes.textLine}>
        <Typography variant='body2'>
          {`${t('sms:message')} ${(numberOfTheMessage + numberNotToShowZero)} ${t('sms:of')} ${totalOfMessagesParts}`}
        </Typography>
      </div>);
    } else {
      return null;
    }
  };

  const getOppositeContent = (timestamp) => {
    return (
      <TimelineOppositeContent>
        <Typography variant='body2' color='textSecondary'>
          {new Date(timestamp).toLocaleString()}
        </Typography>
      </TimelineOppositeContent>
    );
  };

  if (snsData.eventType === 'Send') {
    return (
      <TimelineItem>
        {getOppositeContent(snsData.mail.timestamp)}
        <Separator />
        <TimelineContent>
          <Paper elevation={3} className={classes.paper}>
            <Typography variant='button'>
              {t(`email:eventType:${snsData.eventType}`) }
            </Typography>
            <div className={classes.textLine}>
              <Typography variant='body2'>
                {t(`email:messageId`) }&nbsp;
              </Typography>
              <Typography variant='caption'> {snsData.mail.commonHeaders.messageId}</Typography>
            </div>
            <div className={classes.textLine}>
              <Typography variant='body2'>
                {t(`email:subject`) }&nbsp;
              </Typography>
              <Typography variant='caption'> {snsData.mail.commonHeaders.subject}</Typography>
            </div>
            <div className={classes.textLine}>
              <Typography variant='body2'>
                {t(`email:from`) }&nbsp;
              </Typography>
              <Typography variant='caption'> {snsData.mail.commonHeaders.from.join()}</Typography>
            </div>
            <div className={classes.textLine}>
              <Typography variant='body2'>
                {t(`email:to`) }&nbsp;
              </Typography>
              <Typography variant='caption'> {snsData.mail.commonHeaders.to.join()}</Typography>
            </div>
          </Paper>
        </TimelineContent>
      </TimelineItem>
    );
  } else if (snsData.eventType === 'Delivery') {
    return (
      <TimelineItem>
        {getOppositeContent(snsData.delivery.timestamp)}
        <Separator />
        <TimelineContent>
          <Paper elevation={3} className={classes.paper}>
            <Typography variant='button'>
              {t(`email:eventType:${snsData.eventType}`) }
            </Typography>
            <div className={classes.textLine}>
              <Typography variant='body2'>
                {t(`email:smtpResponse`) }&nbsp;
              </Typography>
              <Tooltip title={snsData.delivery.smtpResponse}>
                <Typography variant='caption'> {snsData.delivery.smtpResponse.slice(0, 40)}...</Typography>
              </Tooltip>
            </div>
            <div className={classes.textLine}>
              <Typography variant='body2'>
                {t(`email:to`) }&nbsp;
              </Typography>
              <Typography variant='caption'> {snsData.delivery.recipients.join()}</Typography>
            </div>
          </Paper>
        </TimelineContent>
      </TimelineItem>
    );
  } else if (snsData.eventType === 'Bounce') {
    return (
      <TimelineItem>
        {getOppositeContent(snsData.bounce.timestamp)}
        <Separator />
        <TimelineContent>
          <Paper elevation={3} className={classes.paper}>
            <Typography variant='button'>
              {t(`email:eventType:${snsData.eventType}`) }
            </Typography>
            <div className={classes.textLine}>
              <Typography variant='body2'>
                {t(`email:bounceType`) }&nbsp;
              </Typography>
              <Typography variant='caption'> {snsData.bounce.bounceType}</Typography>
            </div>
            <div className={classes.textLine}>
              <Typography variant='body2'>
                {t(`email:bounceSubType`) }&nbsp;
              </Typography>
              <Typography variant='caption'> {snsData.bounce.bounceSubType}</Typography>
            </div>
            <div className={classes.textLine}>
              <Typography variant='body2'>
                {t(`email:to`) }&nbsp;
              </Typography>
              <Typography variant='caption'> {snsData.bounce.bouncedRecipients.map(elem => elem.emailAddress).join()}</Typography>
            </div>
            <div className={classes.textLine}>
              <Typography variant='body2'>
                {t(`email:remoteMtaIp`) }&nbsp;
              </Typography>
              <Typography variant='caption'> {snsData.bounce.remoteMtaIp}</Typography>
            </div>
            <div className={classes.textLine}>
              <Typography variant='body2'>
                {t(`email:feedbackId`) }&nbsp;
              </Typography>
              <Typography variant='caption'> {snsData.bounce.feedbackId}</Typography>
            </div>
          </Paper>
        </TimelineContent>
      </TimelineItem>
    );
  } else if (snsData.eventType === 'Complaint') {
    return (
      <TimelineItem>
        {getOppositeContent(snsData.complaint.timestamp)}
        <Separator />
        <TimelineContent>
          <Paper elevation={3} className={classes.paper}>
            <Typography variant='button'>
              {t(`email:eventType:${snsData.eventType}`) }
            </Typography>
            <div className={classes.textLine}>
              <Typography variant='body2'>
                {t(`email:to`) }&nbsp;
              </Typography>
              <Typography variant='caption'> {snsData.complaint.complainedRecipients.map(elem => elem.emailAddress).join()}</Typography>
            </div>
            <div className={classes.textLine}>
              <Typography variant='body2'>
                {t(`email:complaintSubType`) }&nbsp;
              </Typography>
              <Tooltip title={snsData.complaint.complaintSubType}>
                <Typography variant='caption'> {snsData.complaint.complaintSubType}</Typography>
              </Tooltip>
            </div>
            <div className={classes.textLine}>
              <Typography variant='body2'>
                {t(`email:feedbackId`) }&nbsp;
              </Typography>
              <Typography variant='caption'> {snsData.complaint.feedbackId}</Typography>
            </div>
          </Paper>
        </TimelineContent>
      </TimelineItem>
    );
  } else if (snsData.eventType === 'Reject') {
    return (
      <TimelineItem>
        {getOppositeContent(snsData.mail.timestamp)}
        <Separator />
        <TimelineContent>
          <Paper elevation={3} className={classes.paper}>
            <Typography variant='button'>
              {t(`email:eventType:${snsData.eventType}`) }
            </Typography>
            <div className={classes.textLine}>
              <Typography variant='body2'>
                {t(`email:reject`) }&nbsp;
              </Typography>
              <Typography variant='caption'> {snsData.reject.reason}</Typography>
            </div>
          </Paper>
        </TimelineContent>
      </TimelineItem>
    );
  } else if (snsData.eventType === 'Open') {
    return (
      <TimelineItem>
        {getOppositeContent(snsData.open.timestamp)}
        <Separator />
        <TimelineContent>
          <Paper elevation={3} className={classes.paper}>
            <Typography variant='button'>
              {t(`email:eventType:${snsData.eventType}`) }
            </Typography>
            <div className={classes.textLine}>
              <Typography variant='body2'>
                {t(`email:ipAddress`) }&nbsp;
              </Typography>
              <Typography variant='caption'> {snsData.open.ipAddress}</Typography>
            </div>
            <div className={classes.textLine}>
              <Typography variant='body2'>
                {t(`email:userAgent`) }&nbsp;
              </Typography>
              <Tooltip title={snsData.open.userAgent}>
                <Typography variant='caption'> {snsData.open.userAgent.slice(0, 40)}...</Typography>
              </Tooltip>
            </div>
          </Paper>
        </TimelineContent>
      </TimelineItem>
    );
  } else if (snsData.eventType === 'CONNECT') {
    return (
      <TimelineItem>
        {getOppositeContent(audit.createdAt)}
        <Separator />
        <TimelineContent>
          <Paper elevation={3} className={classes.paper}>
            <Typography variant='button'>
              {t(`email:eventType:Open`) }
            </Typography>
            <div className={classes.textLine}>
              <Typography variant='body2'>
                URL:&nbsp;
              </Typography>
              <Typography variant='caption'> {snsData.headers.Origin}/sign/{snsData.queryStringParameters.flowId}/....</Typography>
            </div>
            <div className={classes.textLine}>
              <Typography variant='body2'>
                {t(`email:ipAddress`) }&nbsp;
              </Typography>
              <Typography variant='caption'> {snsData.identity.sourceIp}</Typography>
            </div>
            <div className={classes.textLine}>
              <Typography variant='body2'>
                {t(`email:userAgent`) }&nbsp;
              </Typography>
              <Tooltip title={snsData.identity.userAgent}>
                <Typography variant='caption'> {snsData.identity.userAgent.slice(0, 40)}...</Typography>
              </Tooltip>
            </div>
          </Paper>
        </TimelineContent>
      </TimelineItem>
    );
  } else if (snsData.eventType === 'Click') {
    return (
      <TimelineItem>
        {getOppositeContent(snsData.click.timestamp)}
        <Separator />
        <TimelineContent>
          <Paper elevation={3} className={classes.paper}>
            <Typography variant='button'>
              {t(`email:eventType:${snsData.eventType}`) }
            </Typography>
            <div className={classes.textLine}>
              <Typography variant='body2'>
                {t(`email:ipAddress`) }&nbsp;
              </Typography>
              <Typography variant='caption'> {snsData.click.ipAddress}</Typography>
            </div>
            <div className={classes.textLine}>
              <Typography variant='body2'>
                {t(`email:userAgent`) }&nbsp;
              </Typography>
              <Tooltip title={snsData.click.userAgent}>
                <Typography variant='caption'> {snsData.click.userAgent.slice(0, 40)}...</Typography>
              </Tooltip>
            </div>
            <div className={classes.textLine}>
              <Typography variant='body2'>
                {t(`email:link`) }&nbsp;
              </Typography>
              <Tooltip title={snsData.click.link}>
                <Typography variant='caption'> {snsData.click.link.slice(0, 40)}...</Typography>
              </Tooltip>
            </div>
          </Paper>
        </TimelineContent>
      </TimelineItem>
    );
  } else if (snsData.eventType === 'DeliveryDelay') {
    return (
      <TimelineItem>
        {getOppositeContent(snsData.click.timestamp)}
        <Separator />
        <TimelineContent>
          <Paper elevation={3} className={classes.paper}>
            <Typography variant='button'>
              {t(`email:eventType:${snsData.eventType}`) }
            </Typography>
            <div className={classes.textLine}>
              <Typography variant='body2'>
                {t(`email:delayType`) }&nbsp;
              </Typography>
              <Typography variant='caption'> {snsData.deliveryDelay.delayType}</Typography>
            </div>
            <div className={classes.textLine}>
              <Typography variant='body2'>
                {t(`email:expirationTime`) }&nbsp;
              </Typography>
              <Typography variant='caption'>
                {new Date(snsData.deliveryDelay.expirationTime).toLocaleString()}
              </Typography>
            </div>
            <div className={classes.textLine}>
              <Typography variant='body2'>
                {t(`email:to`) }&nbsp;
              </Typography>
              <Typography variant='caption'>{snsData.deliveryDelay.delayedRecipients.join()}</Typography>
            </div>
          </Paper>
        </TimelineContent>
      </TimelineItem>
    );
  } else if (audit.eventType.includes('smsDeliveryStatus')) {
    return (
      <TimelineItem>
        {getOppositeContent(snsData.notification.timestamp)}
        <Separator />
        <TimelineContent>
          <Paper elevation={3} className={classes.paper}>
            <Typography variant='button'>
              {type === CONSTANTS.STEP.TYPES.otpSignStep ? t(`sms:smsMessageTitleOTP`) : t(`sms:smsMessageTitle`)}
            </Typography>
            {snsData.delivery.numberOfMessageParts > 1 && showNumberOfSplitMessages(snsData)}
            <div className={classes.textLine}>
              <Typography variant='body2'>
                {t(`sms:messageId`)}:&nbsp;
              </Typography>
              <Typography variant='caption'>{snsData.notification.messageId}</Typography>
            </div>
            <div className={classes.textLine}>
              <Typography variant='body2'>
                {t(`sms:to`)}:&nbsp;
              </Typography>
              <Typography variant='caption'>{snsData.delivery.destination}</Typography>
            </div>
            {snsData.delivery.smsType &&
            <div className={classes.textLine}>
              <Typography variant='body2'>
                {t(`sms:type`)}:&nbsp;
              </Typography>
              <Typography variant='caption'>{t(`sms:${snsData.delivery.smsType}`)}</Typography>
            </div>}
            <div className={classes.textLine}>
              <Typography variant='body2'>
                {t(`sms:status`)}:&nbsp;
              </Typography>
              <Typography variant='caption'>{t(`sms:${snsData.status}`)}</Typography>
            </div>
            {snsData.status === 'FAILURE' && (
              <div className={classes.textLine}>
                <Typography variant='body2'>
                  {t(`sms:providerResponse`)}:&nbsp;
                </Typography>
                <Typography variant='caption'>{snsData.delivery.providerResponse}</Typography>
              </div>
            )}
          </Paper>
        </TimelineContent>
      </TimelineItem>
    );
  } else if (audit.eventType === 'stepFunction' && audit.status === 'FAIL' &&
      (audit.type === 'email' || audit.type === 'sms')) {
    return (
      <TimelineItem>
        {getOppositeContent(audit.createdAt)}
        <Separator />
        <TimelineContent>
          <Paper elevation={3} className={classes.paper}>
            <Typography variant='button'>
              {t(`stepFunction:FAIL`) }
            </Typography>
            <div className={classes.textLine}>
              <Typography variant='body2'>
                {t(`stepFunction:type`) }&nbsp;
              </Typography>
              <Typography variant='caption'> {audit.type}</Typography>
            </div>
            <div className={classes.textLine}>
              <Typography variant='body2'>
                {t(`stepFunction:FAIL`) }&nbsp;
              </Typography>
              <Typography variant='caption'> {audit.error.Error}</Typography>
            </div>
            <div className={classes.textLine}>
              <Typography variant='body2'>
                {t(`stepFunction:destination`) }&nbsp;
              </Typography>
              <Typography variant='caption'> {audit.destination}</Typography>
            </div>
          </Paper>
        </TimelineContent>
      </TimelineItem>
    );
  }
  return null;
};

AuditDetail.propTypes = {
  audit: PropTypes.object.isRequired
};

export default AuditDetail;
